import React, { useMemo } from 'react';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import { Images } from '../../../utils/Images';
import { CampaignButtonVariant } from '../../../components/common/Button/Button.jsx';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

const StartNewCampaign = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const isBelow1100 = useMediaQuery('(max-width:1100px)');
  const navigate = useNavigate();
  const home = useSelector((state) => state.cms.home);
  const cms = useMemo(() => {
    const sections = Array.isArray(home?.data) ? home.data : [];
    const section = sections.find((s) => s?.id === 61);
    const related = Array.isArray(section?.relatedCMS) ? section.relatedCMS : [];
    const rawTitle = section?.title;
    const rawDescription = section?.description;
    return {
      // Coerce to string — API sometimes sends numeric ids; empty string should fall back
      title:
        rawTitle != null && String(rawTitle).trim() !== ''
          ? String(rawTitle).trim()
          : '',
      description:
        rawDescription != null && String(rawDescription).trim() !== ''
          ? String(rawDescription).trim()
          : '',
      image: section?.image || "",
      link: section?.link || "",
      points: related.filter(r => r && (r.status === 1 || r.status === true)).map(r => r.description).filter(Boolean).slice(0, 4)
    };
  }, [home]);
  const handleDonateClick = () => {
    if (cms.link) navigate(cms.link);
  };

  const checkpoints = cms.points;

  return (
    <Box
      sx={{
        width: { xs: '95%', sm: '94.44%' },
        maxWidth: '1440px',
        minHeight: { xs: 'auto', md: 'auto' },
        margin: '0 auto',
        marginTop: '40px',
        marginBottom: '15px',
        padding: { 
          xs: '0px 16px', 
          sm: '0px 30px', 
          md: '0px 50px', 
          lg: '0px 77px' 
        },
        position: 'relative'
      }}
    >
      {/* Decorative Elements */}
      {/* Pink Hearts - Top Right */}
      <Box
        component="img"
        src={Images.pinkHearts}
        alt=""
        sx={{
          position: 'absolute',
          top: { xs: '20px', md: '10px' },
          right: { xs: '10px', md: '100px' },
          width: { xs: '40px', sm: '50px', md: '48px' },
          height: 'auto',
          zIndex: 1,
          display: { 
            xs: 'none', 
            md: 'none',
            lg: 'block'
          }
        }}
      />

      {/* Orange Sparks - Bottom Left */}
      <Box
        component="img"
        src={Images.orangeSparks}
        alt=""
        sx={{
          position: 'absolute',
          bottom: { xs: '20px', md: '40px' },
          right: { xs: '10px', md: '34px' },
          width: { xs: '35px', sm: '45px', md: '60px' },
          height: 'auto',
          zIndex: 1,
          display: { 
            xs: 'none', 
            md: 'none',
            lg: 'block'
          }
        }}
      />

      {/* Desktop Layout */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'row',
          height: '100%',
          gap: { md: '40px', lg: '65px' },
          alignItems: 'stretch',
        }}
      >
        {/* Left Section - Image */}
        <Box
          sx={{
            flex: '1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'end',
          }}
        >
          {/* Campaign Image with CSS Mask */}
          <Box
            sx={{
              width: { 
                md: '380px', 
                lg: isBelow1100 ? '420px' : '520px', 
                xl: '520px' 
              },
              height: { 
                md: '385px', 
                lg: isBelow1100 ? '425px' : '528px', 
                xl: '528px' 
              },
              position: 'relative',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            {/* Background Mask Layer */}
            <Box
              component="img"
              src={Images.maskRedBox}
              alt=""
              sx={{
                position: 'absolute',
                width: '80%',
                height: '80%',
                zIndex: 1
              }}
            />
            
            {/* Campaign Image with mask applied */}
            <Box
              component="img"
              src={cms.image || ""}
              alt="Start New Campaign"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                maskImage: `url(${Images.monthlyMaskDesign})`,
                maskSize: '100% 100%',
                maskRepeat: 'no-repeat',
                maskPosition: 'center',
                WebkitMaskImage: `url(${Images.monthlyMaskDesign})`,
                WebkitMaskSize: '100% 100%',
                WebkitMaskRepeat: 'no-repeat',
                WebkitMaskPosition: 'center',
                zIndex: 2
              }}
            />
          </Box>
        </Box>

        {/* Right Section - Content */}
        <Box
          sx={{
            width:"100%",
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            textAlign: 'left'
          }}
        >
          {/* Titles Section */}
          <Box sx={{
            display: 'flex', 
            flexDirection: 'row', 
            gap: '8px', 
            alignItems: 'center',
            justifyContent: 'flex-start',
            marginBottom: '11px'
          }}>
            {/* Yellow Title */}
            <Box
              sx={{
                fontFamily:isMobile? 'Red Hat Display' :'Caveat',
                fontWeight: 700,
                fontStyle: 'normal',
                fontSize: { 
                  md: isBelow1100 ? '16px' : '18px',
                  lg: '18px' 
                },
                lineHeight: { 
                  md: isBelow1100 ? '16px' : '18.09px',
                  lg: '18.09px' 
                },
                letterSpacing: '0%',
                color: '#FFAC00'
              }}
            >
              Start New Campaign
            </Box>

            {/* Yellow Line */}
            <Box
              sx={{
                width: { md: isBelow1100 ? '40px' : '46px', lg: '46px' },
                height: '2px',
                backgroundColor: '#FFAC00'
              }}
            />
          </Box>

          {/* Main Title */}
          <Box
            sx={{
              fontFamily: 'Red Hat Display',
              fontWeight: 800,
              fontStyle: 'normal',
              fontSize: { 
                md: isBelow1100 ? '28px' : '32px', 
                lg: '36px' 
              },
              lineHeight: '130%',
              letterSpacing: '0%',
              textTransform: 'capitalize',
              color: '#1F1F1F',
              marginBottom: '10px'
            }}
          >
            {cms.title}
          </Box>

          {/* Description */}
          <Box
            sx={{
              fontFamily: 'Red Hat Display',
              fontWeight: 400,
              fontStyle: 'normal',
              fontSize: { 
                md: isBelow1100 ? '13px' : '14px',
                lg: '14px' 
              },
              lineHeight: { 
                md: isBelow1100 ? '20px' : '22px',
                lg: '24px' 
              },
              letterSpacing: '0%',
              textAlign: 'left',
              color: '#4A4A49',
              marginBottom: '30px'
            }}
          >
            {cms.description}
          </Box>

          {/* 2x2 Grid of Checkpoints */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '20px',
              marginBottom: '40px'
            }}
          >
            {checkpoints.map((checkpoint, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  justifyContent: 'flex-start'
                }}
              >
                <Box
                  component="img"
                  src={Images.tick}
                  alt="Tick"
                  sx={{
                    width: '19px',
                    height: '19px',
                    objectFit: 'contain'
                  }}
                />
                <Box
                  sx={{
                    fontWeight: 600,
                    fontStyle: 'normal',
                    fontSize: { 
                      md: isBelow1100 ? '12px' : '13.57px',
                      lg: '13.57px' 
                    },
                    lineHeight: '100%',
                    letterSpacing: '0%',
                    color: '#1F1F1F'
                  }}
                >
                  {checkpoint}
                </Box>
              </Box>
            ))}
          </Box>

          {/* Start Campaign Button */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start'
            }}
          >
            <CampaignButtonVariant
              text="Start Campaign"
              onClick={handleDonateClick}
              isMobile={false}
            />
          </Box>
        </Box>
      </Box>

      {/* Mobile/Tablet Layout - Stacked */}
      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          flexDirection: 'column',
          // gap: '40px',
          alignItems: 'center'
        }}
      >
        {/* 1. Titles Section - First */}
        <Box sx={{
          display: 'flex', 
          flexDirection: 'row', 
          gap: '8px', 
          alignItems: 'center',
          justifyContent: 'flex-start',
          marginBottom: '12px',
          width: '100%'
        }}>
          {/* Yellow Title */}
          <Box
            sx={{
              fontFamily: isMobile ? 'Red Hat Display' :'Caveat',
              fontWeight: 700,
              fontStyle: 'normal',
              fontSize: { xs: '12px', sm: '17px' },
              lineHeight: { xs: '12px', sm: '17px' },
              letterSpacing: '0%',
              color: '#FFAC00',
              textAlign: { xs: 'center', sm: 'left' },
              verticalAlign: 'middle'
            }}
          >
            Start New Campaign
          </Box>

          {/* Yellow Line - Hidden on mobile */}
          <Box
            sx={{
              width: { xs: '10px', sm: '43px' },
              height: '2px',
              backgroundColor: '#FFAC00',
              display: { xs: 'none', md: 'block' }
            }}
          />
        </Box>

        {/* Main Title — same CMS field as desktop (was incorrectly hardcoded on mobile/tablet) */}
        <Box
          sx={{
            fontFamily: 'Red Hat Display',
            fontWeight: 800,
            fontStyle: 'normal',
            fontSize: { xs: '18px', sm: '26px' },
            lineHeight: { xs: '140%', sm: '130%' },
            letterSpacing: '0%',
            textTransform: 'capitalize',
            color: '#1F1F1F',
            textAlign: 'left',
            marginBottom: '12px',
            width: '100%',
            verticalAlign: 'middle'
          }}
        >
          {cms.title}
        </Box>

        {/* 2. Image Section - Second */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative'
          }}
        >
          {/* Pink Heart - Top Left of Image */}
          <Box
            component="img"
            src={Images.pinkHearts}
            alt=""
            sx={{
              position: 'absolute',
              top: '20px',
              left: '-15px',
              width: '30px',
              height: 'auto',
              zIndex: 2,
              display: { xs: 'block', md: 'none' },
              transform: 'scaleX(-1)'
            }}
          />

          {/* Pink Heart - Bottom Right of Image */}
          <Box
            component="img"
            src={Images.pinkHearts}
            alt=""
            sx={{
              position: 'absolute',
              bottom: '20px',
              right: '-15px',
              width: '30px',
              height: 'auto',
              zIndex: 2,
              display: { xs: 'block', md: 'none' }
            }}
          />

          {/* Campaign Image with CSS Mask */}
          <Box
            sx={{
              width: { xs: '280px', sm: '320px' },
              height: { xs: '284px', sm: '324px' },
              position: 'relative',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            {/* Background Mask Layer */}
            <Box
              component="img"
              src={Images.maskRedBox}
              alt=""
              sx={{
                position: 'absolute',
                width: '80%',
                height: '80%',
                zIndex: 1
              }}
            />
            
            {/* Campaign Image with mask applied */}
            <Box
              component="img"
              src={cms.image || ""}
              alt="Start New Campaign"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                maskImage: `url(${Images.monthlyMaskDesign})`,
                maskSize: '100% 100%',
                maskRepeat: 'no-repeat',
                maskPosition: 'center',
                WebkitMaskImage: `url(${Images.monthlyMaskDesign})`,
                WebkitMaskSize: '100% 100%',
                WebkitMaskRepeat: 'no-repeat',
                WebkitMaskPosition: 'center',
                zIndex: 2
              }}
            />
          </Box>
        </Box>

        {/* 3. Content Section - Third */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            textAlign: 'left',
            width: '100%'
          }}
        >
          {/* Description */}
          <Box
            sx={{
              fontFamily: 'Red Hat Display',
              fontWeight: 400,
              fontStyle: 'normal',
              fontSize: { xs: '11px', sm: '13.5px' },
              lineHeight: { xs: '19.42px', sm: '21px' },
              letterSpacing: { xs: '-2%', sm: '0%' },
              textAlign: { xs: 'justify', sm: 'left' },
              color: '#4A4A49',
              marginBottom: '24px',
              verticalAlign: 'middle'
            }}
          >
            {cms.description}
          </Box>

          {/* 2x2 Grid of Checkpoints */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: { xs: '16px', sm: '18px' },
              marginBottom: '32px'
            }}
          >
            {checkpoints.map((checkpoint, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  justifyContent: 'flex-start'
                }}
              >
                <Box
                  component="img"
                  src={Images.tick}
                  alt="Tick"
                  sx={{
                    width: { xs: '17px', sm: '17px' },
                    height: { xs: '17px', sm: '17px' },
                    objectFit: 'contain'
                  }}
                />
                <Box
                  sx={{
                    fontWeight: 600,
                    fontStyle: 'normal',
                    fontSize: { xs: '12px', sm: '12.5px' },
                    lineHeight: '100%',
                    letterSpacing: '0%',
                    color: '#1F1F1F'
                  }}
                >
                  {checkpoint}
                </Box>
              </Box>
            ))}
          </Box>

          {/* Start Campaign Button */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            <CampaignButtonVariant
              text="Start Campaign"
              onClick={handleDonateClick}
              isMobile={true}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default StartNewCampaign;
